import { Package, Camera } from 'lucide-react';
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL || '';
const CONDITION_LABELS = { neuf:"Comme neuf", bon:"Bon etat", moyen:"Etat moyen", pieces:"Pour pieces" };
const SERVICE_LABELS = { sav:"Service apres-vente", livraison:"Livraison & Installation", montage:"Montage & Formation" };
const img = (u) => u && u.startsWith('/api') ? API + u : u;

const ChatBox = ({ listing, onClose }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [msgImg, setMsgImg] = useState("");
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem("dz_token");
      const r = await fetch(API + "/api/occasion/messages/" + listing.id, { headers: { "Authorization": "Bearer " + token } });
      if (r.ok) setMessages(await r.json());
    } catch {}
  };
  useEffect(() => { if (user) { fetchMessages(); const i = setInterval(fetchMessages, 4000); return () => clearInterval(i); } }, [listing, user]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const upload = async (file) => {
    setUploading(true);
    try {
      const token = localStorage.getItem("dz_token");
      const data = new FormData(); data.append("file", file);
      const r = await fetch(API + "/api/upload", { method: "POST", headers: { "Authorization": "Bearer " + token }, body: data });
      if (r.ok) { const d = await r.json(); setMsgImg(d.url); }
    } catch {}
    setUploading(false);
  };

  const send = async () => {
    if ((!text.trim() && !msgImg) || sending) return;
    if (!user) { navigate("/connexion"); return; }
    setSending(true);
    try {
      const token = localStorage.getItem("dz_token");
      const r = await fetch(API + "/api/occasion/messages", {
        method: "POST", headers: { "Content-Type": "application/json", "Authorization": "Bearer " + token },
        body: JSON.stringify({ listing_id: listing.id, text: text.trim(), image: msgImg }),
      });
      if (r.ok) { setText(""); setMsgImg(""); fetchMessages(); }
    } catch {}
    setSending(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg flex flex-col" style={{maxHeight:"85vh"}}>
        <div className="bg-[#1F4D3F] px-5 py-4 flex items-center justify-between rounded-t-2xl">
          <div><div className="text-white font-semibold text-sm">Contacter le vendeur</div><div className="text-white/70 text-xs truncate">{listing.title}</div></div>
          <button onClick={onClose} className="text-white/70 hover:text-white text-2xl">×</button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-[#FAFAF8]" style={{minHeight:"300px"}}>
          {messages.length === 0 && <div className="text-center text-sm text-[#9A8F82] py-8">Envoyez un message au vendeur</div>}
          {messages.map(m => (
            <div key={m.id} className={"flex " + (m.from_id === user?.user_id ? "justify-end" : "justify-start")}>
              <div className={"max-w-[80%] px-3 py-2 rounded-xl text-sm " + (m.from_id === user?.user_id ? "bg-[#1F4D3F] text-white" : "bg-white border border-[#E8E0D0] text-[#2D2A26]")}>
                {m.from_id !== user?.user_id && <div className="text-xs font-medium text-[#C75B27] mb-1">{m.from_name}</div>}
                {m.image && <img src={img(m.image)} alt="" className="rounded-lg mb-1 max-w-full" style={{maxHeight:"150px"}} />}
                {m.text}
              </div>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
        {msgImg && (
          <div className="px-3 py-2 bg-[#F5F1EA] flex items-center gap-2">
            <img src={img(msgImg)} alt="" className="w-12 h-12 rounded object-cover" />
            <span className="text-xs text-[#7A6F5C] flex-1">Photo prête</span>
            <button onClick={() => setMsgImg("")} className="text-red-500 text-sm">Retirer</button>
          </div>
        )}
        <div className="p-3 border-t border-[#E8E0D0] flex gap-2 bg-white rounded-b-2xl">
          {user && <label className="cursor-pointer flex items-center px-2"><Camera className="w-5 h-5" /><input type="file" accept="image/*" onChange={e => e.target.files[0] && upload(e.target.files[0])} className="hidden" disabled={uploading} /></label>}
          <input value={text} onChange={e => setText(e.target.value)} onKeyDown={e => e.key === "Enter" && send()}
            placeholder={user ? (uploading ? "Upload..." : "Votre message...") : "Connectez-vous"} disabled={!user}
            className="flex-1 px-3 py-2 border border-[#E8E0D0] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1F4D3F] disabled:bg-[#F5F1EA]" />
          <button onClick={send} disabled={(!text.trim() && !msgImg) || sending || !user} className="bg-[#1F4D3F] text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50">{sending ? "..." : "Envoyer"}</button>
        </div>
      </div>
    </div>
  );
};

const OccasionDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showChat, setShowChat] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(API + "/api/occasion/listings/" + id);
      if (r.ok) {
        const d = await r.json();
        setListing(d);
        setEditForm({ title: d.title, description: d.description, price: d.price || "", condition: d.condition, brand: d.brand, wilaya: d.wilaya, city: d.city });
      }
    } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, [id]);

  const saveEdit = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("dz_token");
      const r = await fetch(API + "/api/occasion/listings/" + id, {
        method: "PATCH", headers: { "Content-Type": "application/json", "Authorization": "Bearer " + token },
        body: JSON.stringify({ ...editForm, price: editForm.price ? parseFloat(editForm.price) : null }),
      });
      if (r.ok) { toast({ title: "✅ Annonce modifiée" }); setEditing(false); load(); }
    } catch {}
    setSaving(false);
  };

  const deleteListing = async () => {
    if (!window.confirm("Supprimer cette annonce ?")) return;
    try {
      const token = localStorage.getItem("dz_token");
      const r = await fetch(API + "/api/occasion/listings/" + id, { method: "DELETE", headers: { "Authorization": "Bearer " + token } });
      if (r.ok) { toast({ title: "Annonce supprimée" }); navigate("/occasion"); }
    } catch {}
  };

  if (loading) return <div className="max-w-4xl mx-auto px-4 py-20 text-center text-[#9A8F82]">Chargement...</div>;
  if (!listing) return <div className="max-w-4xl mx-auto px-4 py-20 text-center"><div className="flex justify-center mb-4"><Package className="w-14 h-14 text-[#C9BFAE]" /></div><p className="text-[#7A6F5C]">Annonce introuvable</p><button onClick={() => navigate("/occasion")} className="mt-4 text-[#1F4D3F] underline">Retour aux annonces</button></div>;

  const images = listing.images || [];
  const isOwner = user && user.user_id === listing.seller_id;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <button onClick={() => navigate("/occasion")} className="flex items-center gap-2 text-[#1F4D3F] text-sm mb-6 hover:underline">← Retour aux annonces</button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Galerie photos */}
        <div>
          <div className="h-80 bg-[#F5F1EA] rounded-2xl flex items-center justify-center overflow-hidden mb-3">
            {images.length > 0 ? <img src={img(images[activeImg])} alt={listing.title} className="w-full h-full object-contain" /> : <Package className="w-14 h-14 text-[#C9BFAE]" />}
          </div>
          {images.length > 1 && (
            <div className="flex gap-2 overflow-x-auto">
              {images.map((im, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={"w-20 h-20 rounded-lg overflow-hidden shrink-0 border-2 " + (activeImg === i ? "border-[#1F4D3F]" : "border-transparent")}>
                  <img src={img(im)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Infos */}
        <div>
          {editing ? (
            <div className="space-y-3">
              <input value={editForm.title} onChange={e => setEditForm(f => ({...f, title: e.target.value}))} className="w-full px-3 py-2 border border-[#E8E0D0] rounded-xl text-sm" placeholder="Titre" />
              <textarea value={editForm.description} onChange={e => setEditForm(f => ({...f, description: e.target.value}))} rows={3} className="w-full px-3 py-2 border border-[#E8E0D0] rounded-xl text-sm resize-none" placeholder="Description" />
              <input value={editForm.price} onChange={e => setEditForm(f => ({...f, price: e.target.value}))} type="number" className="w-full px-3 py-2 border border-[#E8E0D0] rounded-xl text-sm" placeholder="Prix (DA)" />
              <input value={editForm.brand} onChange={e => setEditForm(f => ({...f, brand: e.target.value}))} className="w-full px-3 py-2 border border-[#E8E0D0] rounded-xl text-sm" placeholder="Marque" />
              <div className="flex gap-2">
                <button onClick={saveEdit} disabled={saving} className="flex-1 bg-[#1F4D3F] text-white py-2.5 rounded-xl font-semibold text-sm disabled:opacity-50">{saving ? "..." : "Enregistrer"}</button>
                <button onClick={() => setEditing(false)} className="px-4 border border-[#E8E0D0] rounded-xl text-sm">Annuler</button>
              </div>
            </div>
          ) : (
            <>
              <h1 className="font-serif text-3xl font-semibold text-[#1F4D3F] mb-2">{listing.title}</h1>
              {listing.price ? <div className="text-3xl font-bold text-[#C75B27] mb-4">{Math.round(listing.price).toLocaleString("fr")} DA {listing.price_negotiable && <span className="text-sm font-normal text-[#9A8F82]">(négociable)</span>}</div> : <div className="text-lg text-[#9A8F82] mb-4">Prix sur demande</div>}

              <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                {listing.brand && <div><span className="text-[#9A8F82]">Marque :</span> <strong>{listing.brand}</strong></div>}
                {listing.condition && <div><span className="text-[#9A8F82]">État :</span> <strong>{CONDITION_LABELS[listing.condition]}</strong></div>}
                {listing.wilaya && <div><span className="text-[#9A8F82]">Wilaya :</span> <strong>{listing.wilaya}</strong></div>}
                {listing.city && <div><span className="text-[#9A8F82]">Ville :</span> <strong>{listing.city}</strong></div>}
              </div>

              {(listing.services || []).length > 0 && (
                <div className="mb-4">
                  <h3 className="font-medium text-sm text-[#2D2A26] mb-2">Services inclus</h3>
                  <div className="flex flex-wrap gap-2">
                    {listing.services.map(s => <span key={s} className="text-sm bg-[#E4EDE9] text-[#1F4D3F] px-3 py-1.5 rounded-full">{SERVICE_LABELS[s] || s}</span>)}
                  </div>
                </div>
              )}

              {listing.description && (
                <div className="mb-6">
                  <h3 className="font-medium text-sm text-[#2D2A26] mb-2">Description</h3>
                  <p className="text-sm text-[#5A5247] bg-[#F5F1EA] rounded-xl p-4 whitespace-pre-wrap">{listing.description}</p>
                </div>
              )}

              <div className="flex items-center gap-3 mb-6 p-3 bg-[#E4EDE9] rounded-xl">
                <div className="w-11 h-11 rounded-full bg-[#1F4D3F] flex items-center justify-center text-white font-bold">{(listing.seller_name || "?")[0].toUpperCase()}</div>
                <div><div className="font-medium text-sm">{listing.seller_name}</div><div className="text-xs text-[#7A6F5C]">{listing.seller_role === "supplier" ? "Fournisseur" : "Vendeur"}</div></div>
              </div>

              {isOwner ? (
                <div className="flex gap-2">
                  <button onClick={() => setEditing(true)} className="flex-1 bg-[#1F4D3F] hover:bg-[#173B30] text-white py-3 rounded-xl font-semibold text-sm">✏️ Modifier l'annonce</button>
                  <button onClick={deleteListing} className="px-4 border border-red-300 text-red-600 hover:bg-red-50 rounded-xl font-semibold text-sm">Supprimer</button>
                </div>
              ) : (
                <button onClick={() => user ? setShowChat(true) : navigate("/connexion")} className="w-full bg-[#C75B27] hover:bg-[#a84d21] text-white py-3 rounded-xl font-semibold text-sm">Contacter le vendeur</button>
              )}
            </>
          )}
        </div>
      </div>

      {showChat && <ChatBox listing={listing} onClose={() => setShowChat(false)} />}
    </div>
  );
};

export default OccasionDetail;
